import React, { useContext, useEffect, useMemo } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import reduce from 'lodash/reduce';
import styled from 'styled-components';

import PercentDiscountLabel from '@r1-frontend/ui-react/components/DiscountLabel/PercentDiscountLabel';
import { WithSkeleton, WithSkeletonContext } from '@r1-frontend/ui-react/components/loaders/Skeleton';
import StepTabs from '@r1-frontend/ui-react/components/StepMenu';
import { TStepTabsItem } from '@r1-frontend/ui-react/components/StepMenu/StepTabs';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { IndentContainer, ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import BaseToaster from '@r1-frontend/ui-react/experimental/Toaster/Toaster';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';

import { setStep } from '~/src/features/tariffBuilder/actions';
import { initialization } from '~/src/features/tariffBuilder/actions/thunk';
import RulesBannerBlock from '~/src/features/tariffBuilder/components/RulesBannerBlock';
import steps from '~/src/features/tariffBuilder/constants/steps';
import ProgressBarContainer from '~/src/features/tariffBuilder/containers/ProgressBarContainer';
import SummaryInfoContainer from '~/src/features/tariffBuilder/containers/SummaryInfoContainer';
import { Ctx } from '~/src/features/tariffBuilder/Ctx';
import { useCtxDispatch, useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import getSummaryInfo from '~/src/features/tariffBuilder/selectors/getSummaryInfo';
import { ESteps } from '~/src/features/tariffBuilder/types/ESteps';
import { TLimitations } from '~/src/features/tariffBuilder/types/TLimitations';
import { useScroll } from '~/src/helpers/hooks/useScroll';
import { useValueByBreakpoint } from '~/src/helpers/hooks/useValueByBreakpoint';
import { selectCompanyName } from '~/src/store/selectors/city';

import FinalSummaryInfoContainer from './containers/FinalSummaryInfoContainer';

import { Block, DoubleColumnLayout, RightSide, TabsContainer } from '~/src/features/tariffBuilder/styled';

type TConstructorProps = {
    routerLimitations: TLimitations,
    decoderLimitations: TLimitations,
    withCurrent?: boolean,
    className?: string,
}

const Constructor = ({ routerLimitations, decoderLimitations, withCurrent, ...props }: TConstructorProps): JSX.Element | null => {
    const companyName = useSelector(selectCompanyName);

    const scrollToTop = useScroll({ absolute: 'top' });
    const dispatch = useCtxDispatch();
    const { toaster: toasterData } = useContext(Ctx);

    const { currentStep, summaryInfo, initialized, maximumTakenStep } = useCtxSelector(state => ({
        initialized: state.initialized,
        currentStep: state.currentStep,
        maximumTakenStep: state.maximumTakenStep,
        summaryInfo: getSummaryInfo(state),
    }));

    const verticalIndent = useValueByBreakpoint({
        desktop: 165,
        laptop: 135,
    });

    const Page = steps[currentStep].Page;

    const memoSteps = useMemo(() => {
        return reduce(steps, (result: TStepTabsItem[], step) => {
            if ('label' in step && 'Image' in step) {
                result.push(step);
            }

            return result;
        }, []);
    }, []);

    useEffect(() => {
        dispatch(initialization(routerLimitations, decoderLimitations, withCurrent));
    }, []);

    useEffect(() => {
        scrollToTop(null);
    }, [Page]);

    const handleSelectStep = (stepId: number) => {
        dispatch(setStep(stepId));
    };

    return (
        <div className={props.className}>
            <SkeletonTheme borderRadius={BorderRadius.componentRadius}>
                <WithSkeletonContext isLoading={!initialized}>
                    <BaseToaster
                        position={'tl'}
                        direction={'ttb'}
                        toasts={toasterData.toasts}
                        removeToastHandler={toasterData.remove}
                        $verticalIndent={verticalIndent}
                        $horizontalIndent={100}
                    />
                    <DoubleColumnLayout>
                        <Block>
                            {currentStep < ESteps.FillAddress && (
                                <IndentContainer $desktop={[0, 0, 32]} $mobile={[0, 0, 16]}>
                                    <ListContainer $direction={'column'} $wrap={'nowrap'} $size={0}>
                                        <H3 as='h1'>Тариф Конструктор&nbsp;от {companyName}</H3>
                                        <RulesBannerBlock/>
                                    </ListContainer>
                                </IndentContainer>
                            )}
                            {currentStep > ESteps.Equipment
                                ? <ProgressBarContainer/>
                                : <TabsContainer>
                                    <WithSkeleton
                                        height={35}
                                        width={`${100 / memoSteps.length}%`}
                                        count={memoSteps.length}
                                        inline
                                        containerClassName={'react-skeleton-flex-1 fd-row gap16'}
                                    >
                                        <StepTabs
                                            items={memoSteps}
                                            current={currentStep}
                                            activatedSteps={maximumTakenStep}
                                            onClick={handleSelectStep}
                                        />
                                    </WithSkeleton>
                                    <PercentDiscountLabel>{summaryInfo.discount}</PercentDiscountLabel>
                                </TabsContainer>
                            }
                            <Page/>
                        </Block>

                        <RightSide>
                            {currentStep === ESteps.Final
                                ? <FinalSummaryInfoContainer/>
                                : <SummaryInfoContainer/>
                            }
                        </RightSide>
                    </DoubleColumnLayout>
                </WithSkeletonContext>
            </SkeletonTheme>
        </div>
    );
};

export default styled(Constructor)`
    width: 100%;

    .react-skeleton-flex-1 {
        display: flex;
        flex: 1;
    }

    .react-skeleton-flex-1.fd-row {
        flex-direction: row;
    }

    .react-skeleton-flex-1.gap8 {
        gap: 8px;
    }

    .react-skeleton-flex-1.gap16 {
        gap: 16px;
    }
`;
